import React, { useEffect, useContext } from 'react'
import { Container, Col, Row, Button, Spinner } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { setExitModal, setHeaderKey, setNextClicked, setShowExitModal } from '../../redux/actions/header'
import { saveAssessment } from '../../redux/actions/assessment'
import { generateCarePlan, saveCarePlan } from '../../redux/actions/ccp'
import { saveWrapUp } from '../../redux/actions/wrapup'
import SaveModal from '../Modals/SaveModal'
import PropTypes from 'prop-types'
import { updateSessionStatus } from '../../redux/actions/patient'
import encounterStatusEnum from '../../utils/encounterStatusEnum'
import { AGLContext } from '../../contexts/AglContext'

const steps = ['assessment', 'ccp', 'wrapup']

const Footer = ({ show }) => {
  const agl = useContext(AGLContext)
  const dispatch = useDispatch()

  const {
    encounter, 
    pga, 
    flare,
    flareIsNull,
    s2k, 
    saveAssessmentStatus,
    saveCarePlanStatus, 
    patient,
    assembledNote, 
    healthSystem, 
    damage,
    damageIsNull,
    lldas,
    nextClicked,
    showExitModal,
    exitModal,
    medicationState,
    generatingCarePlan,
    generatedCarePlan,
    opportunities,
    rules,
    actions,
    subActions,
  } = useSelector((state) => ({
    encounter: state.patient.encounter,
    pga: state.assessment.pga,
    flare: state.assessment.flare,
    flareIsNull: state.assessment.flareIsNull,
    s2k: state.assessment.s2k,
    saveAssessmentStatus: state.assessment.saveAssessment,
    patient: state.patient.patient,
    saveCarePlanStatus: state.ccp.saveCarePlan,
    assembledNote: state.ccp.assembledNote,
    healthSystem: state.healthSystem.healthSystem,
    damage: state.assessment.damage,
    damageIsNull: state.assessment.damageIsNull,
    lldas: state.assessment.lldas,
    nextClicked: state.header.nextClicked,
    showExitModal: state.header.showExitModal,
    exitModal: state.header.exitModal,
    medicationState: state.patient.getMedications,
    generatingCarePlan: state.ccp.isFetching,
    opportunities: state.ccp.opportunities,
    rules: state.ccp.rules,
    actions: state.ccp.actions,
    subActions: state.ccp.subActions,
    generatedCarePlan: state.ccp.isSuccess
  }))

  const activeKey = useSelector((state) => state.header.activeKey)
  const index = steps.indexOf(activeKey)

  const save = async (btnType) => {
    if (btnType === 'exit') {
      dispatch(setExitModal({
        title: 'Exit',
        content: 'Assessment data will be saved on exit. Are you sure you want to close the app?',
        kind: 'Exit'
      }))
      dispatch(setShowExitModal(true))
    } else {
      if (btnType === 'next') {
        dispatch(setNextClicked(true))
      }

      switch (activeKey) {
      case 'assessment':
        await dispatch(saveAssessment(encounter, pga, flare, s2k, damage, lldas, flareIsNull, damageIsNull))
        await dispatch(generateCarePlan(encounter.encounterId, patient, healthSystem.healthSystemId))
        await dispatch(updateSessionStatus(encounterStatusEnum.RECC.value))
        break
      case 'ccp':
        await dispatch(saveCarePlan(encounter, opportunities, rules, actions, subActions, agl.aglState.orders))
        await dispatch(updateSessionStatus(encounterStatusEnum.REVIEW.value))
        break
      case 'wrapup': {
        await dispatch(saveCarePlan(encounter, opportunities, rules, actions, subActions, agl.aglState.orders))
        await dispatch(saveWrapUp(encounter, assembledNote))
      }
        break
      default:
        break
      }
    }
  }

  useEffect(() => {
    if (!nextClicked) return
  
    if (saveCarePlanStatus.success || generatedCarePlan) {
      nextClick()
    }
  }, [nextClicked, saveCarePlanStatus.success, generatedCarePlan])

  const nextClick = () => {
    dispatch(setHeaderKey(steps[index + 1]))
    dispatch(setNextClicked(false))
  }

  const backClick = () => {
    dispatch(setHeaderKey(steps[index - 1]))
  }

  const handleFinishClick = () => {
    dispatch(setExitModal({
      title: 'Finished',
      content: 'Are you sure you want to send the information to Epic? All data will be saved.',
      kind: 'Confirm'
    }))
    dispatch(setShowExitModal(true))
  }

  return (
    <footer>
      <Container fluid>
        {
          show && (
            <>
              <Row>
                <Col>
                  <Button variant='outline-primary' disabled={activeKey === 'assessment'} onClick={backClick}>Back</Button>
                </Col>
                <Col className='text-center'>
                  <Button variant='outline-primary mr-1' onClick={() => save('exit')}>Save and Exit</Button>
                </Col>
                <Col className='text-right'>
                  {
                    nextClicked && (saveAssessmentStatus.loading || saveCarePlanStatus.loading || generatingCarePlan)
                      ? (
                        <>
                          <Spinner animation='border' size='sm' />
                          <span className='ml-2'>Processing Data...</span>
                        </>
                      )
                      : (
                        <Button
                          variant='primary'
                          onClick={() => activeKey !== 'wrapup' ? save('next') : handleFinishClick()}
                          disabled={nextClicked && (saveAssessmentStatus.loading || saveCarePlanStatus.loading || medicationState.loading || generatingCarePlan)}
                        >
                          {
                            activeKey !== 'wrapup'
                              ? 'Next >>'
                              : 'FINISH'
                          }
                        </Button>
                      )
                  }
                </Col>
              </Row>
              <SaveModal
                onClose={() => dispatch(setShowExitModal(false))} 
                show={showExitModal} 
                title={exitModal.title} 
                bodyContent={exitModal.content} 
                kind={exitModal.kind} 
              />
            </>
          )
        }
      </Container>
    </footer>
  )
}

Footer.propTypes = {
  show: PropTypes.bool
}

export default Footer
